import React, { useState, useEffect, useRef } from 'react';
import { useToken } from '../App';
import { API_ENDPOINTS } from '../config/api';
import './GuestServices.css';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { FaPhoneAlt, FaPhoneSlash, FaUser, FaMicrophone, FaMicrophoneSlash, FaTh } from 'react-icons/fa';
import { useHotelTitle } from '../hooks/useHotelTitle';
// Use global Janus loaded via <script> in public/index.html
// @ts-ignore
// Remove: const Janus = window.Janus;

// Utility to dynamically load a script
function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.body.appendChild(script);
  });
}

const CallModal: React.FC<{
  show: boolean;
  onHide: () => void;
  serviceName: string;
  serviceNumber: string;
  janusDetails: any;
  sipPlugin: any;
}> = ({ show, onHide, serviceName, serviceNumber, janusDetails, sipPlugin }) => {
  const [callState, setCallState] = useState<'idle'|'connecting'|'in_call'|'ended'|'error'>('idle');
  const [callTime, setCallTime] = useState(0); // seconds
  const [showKeypad, setShowKeypad] = useState(false);
  const [muted, setMuted] = useState(false);
  const callHandle = useRef<any>(null);
  const timerRef = useRef<any>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);

  // Timer logic
  useEffect(() => {
    if (callState === 'in_call') {
      timerRef.current = setInterval(() => setCallTime(t => t + 1), 1000);
    } else {
      clearInterval(timerRef.current);
      if (callState === 'connecting') setCallTime(0);
    }
    return () => clearInterval(timerRef.current);
  }, [callState]);

  // Format timer
  const formatTime = (sec: number) => {
    const m = Math.floor(sec / 60).toString().padStart(2, '0');
    const s = (sec % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const playRemoteAudio = () => {
    const audio = audioRef.current;
    if (!audio || !remoteStreamRef.current) return;
    try {
      audio.srcObject = remoteStreamRef.current;
      audio.autoplay = true;
      audio.muted = false;
      audio.volume = 1.0;
      const playPromise = audio.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.catch((err: any) => {
          console.error('Failed to play Janus remote audio:', err);
        });
      }
    } catch (e) {
      console.error('Error while playing Janus remote audio:', e);
    }
  };

  useEffect(() => {
    if (show && sipPlugin && janusDetails && serviceNumber) {
      setCallState('connecting');
      setCallTime(0);
      setShowKeypad(false);
      setMuted(false);
      // Initiate SIP call using Janus SIP plugin API
      sipPlugin.createOffer({
        tracks: [{ type: 'audio', capture: true, recv: true }],
        success: function(jsep: any) {
          const body = { request: 'call', uri: `sip:${serviceNumber}@${janusDetails.pbx_domain}` };
          sipPlugin.send({ message: body, jsep });
        },
        error: function(error: any) {
          setCallState('error');
          console.error('WebRTC error:', error);
        }
      });
      // Listen for events
      sipPlugin.onmessage = (msg: any, jsep: any) => {
        if (msg && msg.result && msg.result.event) {
          if (msg.result.event === 'accepted') setCallState('in_call');
          if (msg.result.event === 'hangup') setCallState('ended');
        }
        if (jsep) {
          sipPlugin.handleRemoteJsep({ jsep });
        }
      };
      // Handle hangup event from Janus plugin
      sipPlugin.onhangup = () => {
        setCallState('ended');
        // Optionally close the modal automatically:
        // onHide();
      };
      sipPlugin.onremotestream = (stream: MediaStream) => {
        // Legacy Janus callback support
        remoteStreamRef.current = stream;
        playRemoteAudio();
      };
      sipPlugin.onremotetrack = (track: MediaStreamTrack, mid: string, on: boolean) => {
        // Newer Janus callback support
        if (!on) return;
        if (track.kind !== 'audio') return;
        if (!remoteStreamRef.current) {
          remoteStreamRef.current = new MediaStream();
        }
        remoteStreamRef.current.addTrack(track);
        playRemoteAudio();
      };
      callHandle.current = sipPlugin;
    }
    return () => {
      // Hangup on close
      if (callHandle.current) {
        callHandle.current.hangup();
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.srcObject = null;
      }
      remoteStreamRef.current = null;
      clearInterval(timerRef.current);
    };
    // eslint-disable-next-line
  }, [show, sipPlugin, janusDetails, serviceNumber]);

  // Mute (local mic) and DTMF keypad handlers
  const handleMute = () => {
    setMuted(m => {
      const next = !m;
      if (callHandle.current && typeof callHandle.current.muteAudio === 'function') {
        if (next) {
          callHandle.current.muteAudio();
        } else if (typeof callHandle.current.unmuteAudio === 'function') {
          callHandle.current.unmuteAudio();
        }
      }
      return next;
    });
  };
  const handleKeypad = () => setShowKeypad(k => !k);

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          {callState === 'in_call' ? 'Connected' : callState === 'ended' ? 'Call Ended' : callState === 'error' ? 'Call Error' : 'Calling...'} {serviceName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="text-center">
        {/* Connecting State */}
        {callState === 'connecting' && (
          <>
            <div className="calling-animation" style={{ margin: '0 auto 20px' }}>
              <FaPhoneAlt color="#28a745" size={30} />
            </div>
            <div className="calling-text">Connecting to <span>{serviceName}</span></div>
            <div className="calling-subtext">Please wait while we connect your call</div>
            <div className="call-timer mt-2 mb-3">{formatTime(callTime)}</div>
            <Button variant="danger" className="btn-end-call" onClick={onHide}>
              <FaPhoneSlash color="#fff" size={18} /> <span style={{ marginLeft: 6 }}>End Call</span>
            </Button>
          </>
        )}
        {/* In Call State */}
        {callState === 'in_call' && (
          <>
            <div className="connected-animation" style={{ margin: '0 auto 20px' }}>
              <FaUser color="#28a745" size={30} />
            </div>
            <div className="calling-text">Connected to <span>{serviceName}</span></div>
            <div className="call-timer mt-2 mb-4">{formatTime(callTime)}</div>
            <div className="call-controls mb-2">
              <div className="row justify-content-center">
                <div className="col-6 text-center">
                  <button className={`btn btn-control${muted ? ' active' : ''}`} onClick={handleMute}>
                    {muted ? <FaMicrophoneSlash /> : <FaMicrophone />}
                  </button>
                  <div className="control-label">{muted ? 'Unmute' : 'Mute'}</div>
                </div>
                <div className="col-6 text-center">
                  <button className={`btn btn-control${showKeypad ? ' active' : ''}`} onClick={handleKeypad}>
                    <FaTh />
                  </button>
                  <div className="control-label">Keypad</div>
                </div>
              </div>
            </div>
            {/* Keypad UI (optional, can be implemented later) */}
            {showKeypad && (
              <div className="keypad mt-4">
                <div className="row g-2 mb-2">
                  <div className="col-4"><button className="btn btn-keypad">1</button></div>
                  <div className="col-4"><button className="btn btn-keypad">2</button></div>
                  <div className="col-4"><button className="btn btn-keypad">3</button></div>
                </div>
                <div className="row g-2 mb-2">
                  <div className="col-4"><button className="btn btn-keypad">4</button></div>
                  <div className="col-4"><button className="btn btn-keypad">5</button></div>
                  <div className="col-4"><button className="btn btn-keypad">6</button></div>
                </div>
                <div className="row g-2 mb-2">
                  <div className="col-4"><button className="btn btn-keypad">7</button></div>
                  <div className="col-4"><button className="btn btn-keypad">8</button></div>
                  <div className="col-4"><button className="btn btn-keypad">9</button></div>
                </div>
                <div className="row g-2">
                  <div className="col-4"><button className="btn btn-keypad">*</button></div>
                  <div className="col-4"><button className="btn btn-keypad">0</button></div>
                  <div className="col-4"><button className="btn btn-keypad">#</button></div>
                </div>
              </div>
            )}
            <Button variant="danger" className="btn-end-call mt-4" onClick={onHide}>
              <FaPhoneSlash color="#fff" size={18} /> <span style={{ marginLeft: 6 }}>End Call</span>
            </Button>
          </>
        )}
        {/* Ended/Error State */}
        {(callState === 'ended' || callState === 'error') && (
          <>
            <div className="calling-animation" style={{ margin: '0 auto 20px' }}>
              <FaPhoneSlash color="#dc3545" size={30} />
            </div>
            <div className="calling-text">{callState === 'ended' ? 'Call ended.' : 'Call failed.'}</div>
            <Button variant="secondary" style={{ marginTop: 12 }} onClick={onHide}>Close</Button>
          </>
        )}
        <audio id="janus-remote-audio" ref={audioRef} autoPlay playsInline />
      </Modal.Body>
    </Modal>
  );
};

const GuestServices: React.FC = () => {
  const { hotelId, roomId } = useToken()!;
  const { hotelName } = useHotelTitle('Services');
  const [loading, setLoading] = useState(true);
  const [dialerOptions, setDialerOptions] = useState<any[]>([]);
  const [callModal, setCallModal] = useState<{ show: boolean, serviceName: string, serviceNumber: string, janusDetails: any }>({ show: false, serviceName: '', serviceNumber: '', janusDetails: null });
  const [janusDetails, setJanusDetails] = useState<any>(null);
  const [janusRegState, setJanusRegState] = useState<'idle'|'registering'|'registered'|'error'>('idle');
  const [sipPlugin, setSipPlugin] = useState<any>(null);
  const janusInstance = useRef<any>(null);

  // Fetch Janus details and register on mount
  useEffect(() => {
    if (!hotelId || !roomId) return;
    setJanusRegState('registering');
    Promise.all([
      loadScript('/js/janus/adapter.min.js'),
      loadScript('/js/janus/janus.js')
    ])
      .then(() => {
        fetch(API_ENDPOINTS.ROOM_GUEST_JANUS_DETAILS(hotelId, roomId))
          .then(res => res.json())
          .then(details => {
            setJanusDetails(details);
            console.log('Janus details:', details);
            // @ts-ignore
            const Janus = window.Janus;
            const janusServerUrl = `https://${details.janus_domain}/janus`;
            try {
              Janus.init({ debug: "all", callback: () => {
                janusInstance.current = new Janus({
                  server: janusServerUrl,
                  iceServers: [{urls: "stun:stun.l.google.com:19302"}],
                  success: () => {
                    janusInstance.current.attach({
                      plugin: 'janus.plugin.sip',
                      success: (pluginHandle: any) => {
                        setSipPlugin(pluginHandle);
                        // Register SIP
                        pluginHandle.send({
                          message: {
                            request: 'register',
                            username: `sip:${details.sip_username}@${details.pbx_domain}`,
                            secret: details.sip_password,
                            proxy: `sip:${details.pbx_domain}`,
                          }
                        });
                        setJanusRegState('registered');
                      },
                      error: (err: any) => {
                        setJanusRegState('error');
                        setSipPlugin(null);
                        console.error('Janus SIP plugin error:', err);
                      },
                      consentDialog: () => {},
                      webrtcState: () => {},
                      onmessage: (msg: any, jsep: any) => {},
                      onlocalstream: () => {},
                      onremotestream: () => {},
                      oncleanup: () => {},
                    });
                  },
                  error: (err: any) => {
                    setJanusRegState('error');
                    console.error('Janus connection error:', err);
                  },
                  destroyed: () => setJanusRegState('idle'),
                });
              }});
            } catch (e) {
              setJanusRegState('error');
              console.error('Janus init error:', e);
            }
          })
          .catch((err) => {
            setJanusDetails(null);
            setJanusRegState('error');
            console.error('Failed to fetch Janus details:', err);
          });
      })
      .catch((err) => {
        setJanusRegState('error');
        console.error('Failed to load Janus or adapter:', err);
      });
    return () => {
      if (janusInstance.current) {
        janusInstance.current.destroy();
      }
    };
  }, [hotelId, roomId]);

  useEffect(() => {
    if (!hotelId) return;
    setLoading(true);
    fetch(API_ENDPOINTS.ROOM_GUEST_SERVICES(hotelId))
      .then(res => res.json())
      .then(data => {
        setDialerOptions(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => {
        setDialerOptions([]);
        setLoading(false);
      });
  }, [hotelId]);

  const handleCallClick = (option: any) => {
    if (!hotelId || !roomId) return;
    setCallModal({ show: true, serviceName: option.name, serviceNumber: option.number, janusDetails });
  };

  return (
    <div className={loading ? 'loading' : 'loaded'}>
      {/* Janus registration state indicator */}
      <div style={{ position: 'fixed', top: 0, right: 0, zIndex: 2001, padding: 8 }}>
        {janusRegState === 'registering' && <span className="badge bg-warning text-dark">Registering phone...</span>}
        {janusRegState === 'registered' && <span className="badge bg-success">Phone Ready</span>}
        {janusRegState === 'error' && <span className="badge bg-danger">Phone Error</span>}
      </div>
      {/* Loading Overlay */}
      {loading && (
        <div id="loadingOverlay" className="loading-overlay">
          <div className="loading-content">
            <div className="loading-spinner">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
            <div className="loading-text">
              <h3>Connecting to Server</h3>
              <p>Please wait while we establish a secure connection...</p>
            </div>
            <div className="loading-progress">
              <div className="progress">
                <div id="loadingProgressBar" className="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" aria-valuenow={0} aria-valuemin={0} aria-valuemax={100} style={{ width: '0%' }}></div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Content */}
      <div className="content" style={{ maxWidth: 600, margin: '0 auto', padding: 12, marginTop: 80 }}>
        <h2 className="section-title mb-3" style={{ fontSize: 19, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 22, color: '#4a6cf7' }}><i className="fas fa-concierge-bell"></i></span>
          Hotel Services
        </h2>
        <div className="dialer-options">
          {Array.isArray(dialerOptions) && dialerOptions.length === 0 ? (
            <div className="no-options">
              <i className="fas fa-exclamation-circle"></i>
              <h3>No services available</h3>
              <p>There are currently no services available for this hotel.</p>
            </div>
          ) : (
            <>
              {Array.isArray(dialerOptions) && dialerOptions.map(option => (
                <div className="dialer-card" key={option.id}>
                  <div className="dialer-card-body">
                    <div className="dialer-icon">
                      <i className="fas fa-headset"></i>
                    </div>
                    <div className="dialer-content">
                      <h3 className="dialer-title">{option.name}</h3>
                      {option.description && (
                        <p className="dialer-description">{option.description}</p>
                      )}
                    </div>
                    <div className="dialer-action">
                      <button type="button" className="btn btn-call" onClick={() => handleCallClick(option)} disabled={!sipPlugin}>
                        <i className="fas fa-phone"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
      {/* Call Modal */}
      <CallModal
        show={callModal.show}
        onHide={() => setCallModal({ ...callModal, show: false })}
        serviceName={callModal.serviceName}
        serviceNumber={callModal.serviceNumber}
        janusDetails={callModal.janusDetails}
        sipPlugin={sipPlugin}
      />
      {/* Footer */}
      <div className="footer">&copy; {new Date().getFullYear()} QRoom. All Rights Reserved.</div>
    </div>
  );
};

export default GuestServices; 